import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { color, borderColor } from 'styled-system';

import { themeGet } from '@styled-system/theme-get';

import Account from './components/Account.jsx';
import MobileMenu from './components/MobileMenu.jsx';
import TileSearch from './components/TileSearch.jsx';
import Tooltip from '../../shared/Tooltip';
import BoardSelect from './components/BoardSelect.jsx';
import GuestButtons from './components/GuestButtons.jsx';
import useDropdown from '../../lib/hooks/useDropdown';
import LanguageWrapper from './components/LanguageWrapper.jsx';
import ToggleNavBar from './components/ToggleNavBar.jsx';
import { isAdmin } from '../../lib/utils/roles';
import ChatButton from './components/ChatButton.jsx';

// The translate button is show only on the following paths
// host.com/tiles , host.com/board/:public_slug
const showLanguageWrapper = () => {
  const ALLOWED_PATHS_REGEX = /(\/ard\/.+\/(tiles|board))|(^\/(tiles|board))/;
  const path = window.location.pathname;
  return ALLOWED_PATHS_REGEX.test(path);
};

export default function Header({
  logo,
  shouldConvertGuest,
  hideSignIn,
  guestUserResetPath,
  home,
}) {
  const {
    dropdownRef,
    buttonRef,
    showDropdown,
    setShowDropdown,
  } = useDropdown();

  const { userType, browserLanguage, isRequestedLanguageDefault } = useSelector(
    state => state.currentUser,
  );

  return (
    <Wrapper>
      {isAdmin(userType) && <ToggleNavBar />}
      <LogoLink href={home}>
        {logo === 'false' ? (
          <LogoPlaceholder
            id="logoPlaceholder"
            color="black.40"
            borderColor="black.10"
            bg="black.5"
          >
            Logo
          </LogoPlaceholder>
        ) : (
          <Logo as="img" src={logo} />
        )}
      </LogoLink>
      {userType !== 'guest' ? (
        <>
          <BoardSelect userType={userType} />
          <TileSearch />
          <ChatButton />
          <Account
            userType={userType}
            setShowDropdown={setShowDropdown}
            showDropdown={showDropdown}
            buttonRef={buttonRef}
            dropdownRef={dropdownRef}
          />
          {!showDropdown && (
            <>
              <Tooltip id="SearchButton" />
              <Tooltip id="Account" />
            </>
          )}
        </>
      ) : (
        <>
          <GuestButtons
            guestUserResetPath={guestUserResetPath}
            shouldConvertGuest={shouldConvertGuest}
            hideSignIn={hideSignIn}
          />
          <ChatButton />
        </>
      )}
      {showLanguageWrapper() && (
        <LanguageWrapper
          browserLanguage={browserLanguage}
          isRequestedLanguageDefault={isRequestedLanguageDefault}
        />
      )}
      <MobileMenu
        userType={userType}
        guestUserResetPath={guestUserResetPath}
        shouldConvertGuest={shouldConvertGuest}
        hideSignIn={hideSignIn}
      />
    </Wrapper>
  );
}

const Wrapper = styled.header`
  height: 40px;
  display: flex;
  padding: 8px 16px;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  justify-content: space-between;
  align-items: center;
  z-index: 2;

  ${themeGet('mediaQueries.forMediumUp')} {
    height: 70px;
    padding: 0 40px 0 0;
    background-color: ${themeGet('colors.white')};
    border-bottom: 1px solid ${themeGet('colors.black.10')};
    box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.04);
  }
`;

const LogoLink = styled.a`
  ${themeGet('mediaQueries.forMediumUp')} {
    padding-left: 24px;
  }
`;

const Logo = styled.div`
  border-radius: 8px;
  max-width: 86px;
  max-height: 24px;

  ${themeGet('mediaQueries.forMediumUp')} {
    max-width: 150px;
    max-height: 55px;
  }
`;

const LogoPlaceholder = styled(Logo)`
  ${color};
  ${borderColor};
  border: 1px solid;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: 18px;
  width: 86px;
  height: 24px;

  ${themeGet('mediaQueries.forMediumUp')} {
    width: 150px;
    height: 55px;
  }
`;
